import settings from '@/constants/constants';
import { useGetPharmacyOnboarding } from '@/hooks/onboarding/useGetPharmacyOnboarding';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { isValidABN } from '@montugroup/circuit-shared';
import { PharmacyOnboardAddressForm } from '@montugroup/circuit-shared/components/index';
import FormRowSkeletonLoader from '@montugroup/circuit-shared/utils/FormRowSkeletonLoader';
import { TextField, Typography } from '@montugroup/design-system';
import { Box, Grid2, Skeleton, Stack } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

function BusinessInformation() {
  const shouldBlockExternalTerritoryPostcodes = useFeatureFlag('enable_blocking_external_territories');
  const { token } = useParams();
  const { isLoading } = useGetPharmacyOnboarding({ token });
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext();

  const validateABN = (value: string) => {
    return isValidABN(value) || 'Invalid ABN';
  };

  if (isLoading) {
    return (
      <Box data-testid="business-info-skeleton">
        <Typography variant="subtitle2" gutterBottom>
          Pharmacy Business Information
        </Typography>
        <Stack gap={(theme) => theme.spacing(3)}>
          <FormRowSkeletonLoader rows={2} size={6} />
          <Grid2 size={{ xs: 12 }}>
            <Skeleton variant="rectangular" height={56} />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <Skeleton variant="rectangular" height={56} />
          </Grid2>
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <Typography variant="subtitle1">Pharmacy Business Information</Typography>
      <Grid2 container rowSpacing={4} columnSpacing={2}>
        <Grid2 size={{ xs: 12, sm: 6 }} sx={{ display: 'grid' }}>
          <TextField
            label="Pharmacy Trading Name"
            error={!!errors.pharmacyTradingName}
            helperText={
              (errors.pharmacyTradingName?.message as string) ??
              'This name helps our delivery partners locate your pharmacy.'
            }
            InputLabelProps={{ shrink: true }}
            {...register('pharmacyTradingName', { required: 'Please enter the pharmacy trading name.' })}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }} sx={{ display: 'grid' }}>
          <Controller
            name="abn"
            control={control}
            rules={{ required: 'Please enter ABN', validate: validateABN }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                label="ABN"
                InputLabelProps={{ shrink: true }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? ''}
                onChange={(e) => field.onChange(e.target.value.replace(/\s+/g, ''))}
                onPaste={(e) => {
                  e.preventDefault();
                  field.onChange(e.clipboardData.getData('text').replace(/\s+/g, ''));
                }}
              />
            )}
          />
        </Grid2>
        <Grid2 size={{ xs: 12 }} sx={{ display: 'grid' }}>
          <PharmacyOnboardAddressForm
            googlePlacesApiKey={settings.googlePlacesApiKey}
            shouldBlockExternalTerritoryPostcodes={!!shouldBlockExternalTerritoryPostcodes}
          />
        </Grid2>
      </Grid2>
    </>
  );
}

export default BusinessInformation;
